import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import { setTranslationLocally } from "store/liveOps/loyalty/levels/slice";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import TextEditor from "components/wysiwyg/Wysiwyg";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

interface Props {
  activeLocale: string;
  setActiveLocale: (e: string) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  isReadOnly: boolean;
}

const LoyaltyProgramLevelsContentTab: React.FC<Props> = ({
  activeLocale,
  setActiveLocale,
  fieldErrors,
  clearFieldError,
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const localesList = useAppSelector((store) => store.settings.locales);
  const translation = useAppSelector(
    (store) => store["loyalty-levels"].translations[activeLocale],
  );
  const [isLocaleLoading, setIsLocaleLoading] = useState(false);

  const handleFieldChange = (
    key: keyof typeof translation,
    value: string | number | boolean,
  ) => {
    dispatch(
      setTranslationLocally({
        locale: activeLocale,
        data: {
          ...translation,
          locale: activeLocale,
          [key]: value,
        },
      }),
    );
  };

  useEffect(() => {
    if (translation) {
      setIsLocaleLoading(false);
    }
  }, [translation]);

  return (
    <div className="tournament-rules">
      <div className="game-modal-tabs bonus-currencies-tabs small">
        {localesList?.map((el) => (
          <div
            className={`tab-item ${activeLocale === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              setIsLocaleLoading(true);
              setActiveLocale(el);
            }}
          >
            <img
              src={`https://flagpedia.net/data/flags/icon/16x12/${el
                .split("-")[1]
                ?.toLowerCase()}.png`}
              alt=""
            />
            {t(el)}
          </div>
        ))}
      </div>
      {isLocaleLoading ? (
        <ModalTabSkeleton />
      ) : (
        <div className="tournament-rules-form">
          <div className="row">
            <div className="bonus-form-box--item" style={{ flex: 1 }}>
              <InnerInput
                value={translation?.title ?? ""}
                placeholder={t("enterTitle")}
                label={t("title")}
                type="text"
                id="bonus-translation-title"
                disabled={!activeLocale || isReadOnly}
                onChange={(e) => {
                  handleFieldChange("title", e);
                  clearFieldError("title");
                }}
                hasError={Boolean(fieldErrors?.["title"])}
              />
            </div>
            <div className="bonus-form-box--item">
              <SwitchBtn
                activeTxt={t("active")}
                isActive={!!translation?.isActive}
                handleClick={() =>
                  handleFieldChange("isActive", translation?.isActive ? 0 : 1)
                }
                disabled={isReadOnly}
              />
            </div>
          </div>
          <div className="bonus-form-box--item">
            <InnerInput
              maxCodePoints={2500}
              value={translation?.description ?? ""}
              placeholder={t("enterText")}
              label={t("description")}
              type="textarea"
              id="bonus-translation-description"
              disabled={!activeLocale || isReadOnly}
              onChange={(e) => {
                handleFieldChange("description", e);
                clearFieldError("description");
              }}
              hasError={Boolean(fieldErrors?.["description"])}
            />
          </div>
          <div className="bonus-form-box--item html-editor">
            <div className="label">{t("content")}</div>
            <TextEditor
              value={translation?.content ?? ""}
              disabled={!activeLocale || isReadOnly}
              onChange={(e) => {
                handleFieldChange("content", e);
                clearFieldError("content");
              }}
              hasError={Boolean(fieldErrors?.["content"])}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LoyaltyProgramLevelsContentTab;
